import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
    FiDownload, FiEye, FiUser, FiMapPin,
    FiBriefcase, FiDollarSign, FiPhone, FiFileText, FiArrowLeft,
    FiSearch, FiFilter, FiX, FiEdit2, FiTrash2
} from 'react-icons/fi';
import axios from 'axios';
import './ProfilePage.css';
import toast from 'react-hot-toast';
import { fmtDate } from '../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const TABS = [
    { key: 'personal', label: 'Personal Information', icon: <FiUser /> },
    { key: 'address', label: 'Home Address', icon: <FiMapPin /> },
    { key: 'job', label: 'Job Information', icon: <FiBriefcase /> },
    { key: 'salary', label: 'Salary Information', icon: <FiDollarSign /> },
    { key: 'emergency', label: 'Emergency Contact', icon: <FiPhone /> },
    { key: 'documents', label: 'Documents', icon: <FiFileText /> },
];

const fmt = (val) => (val != null && val !== '' ? String(val) : '—');
const formatDate = (d) => fmtDate(d);
const formatSalary = (s) => s != null ? `₹ ${Number(s).toLocaleString('en-IN')}` : '—';

const ReadOnly = ({ label, value }) => (
    <div className="ep-info-item">
        <label>{label}</label>
        <p>{value != null && value !== '' ? String(value) : '—'}</p>
    </div>
);

const getFileExtension = (filePath) => {
    if (!filePath) return '';
    return filePath.split('.').pop().toLowerCase();
};

/* ──────────────────────────────────────────── */
const ViewEmployeeProfile = () => {

    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [employee, setEmployee] = useState(location.state?.employee || null);
    const [activeTab, setActiveTab] = useState('personal');

    /* docs */
    const [allDocuments, setAllDocuments] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);

    /* filter */
    const [docSearch, setDocSearch] = useState('');
    const [docCategory, setDocCategory] = useState('all');
    const [docType, setDocType] = useState('');
    const [docStatus, setDocStatus] = useState('');
    const [showDocFilter, setShowDocFilter] = useState(false);

    /* ref wrapping the entire search + filter area for outside-click */
    const filterAreaRef = useRef(null);

    /* preview */
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');

    /* per-button spinner */
    const [actionLoading, setActionLoading] = useState({});

    /* ── Edit certificate modal ── */
    const [editDoc, setEditDoc] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    /* ── Success toast ── */
    const [toast, setToast] = useState(null); // { msg, type: 'success'|'error' }
    const toastTimer = useRef(null);

    const showToast = (msg, type = 'success') => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ msg, type });
        toastTimer.current = setTimeout(() => setToast(null), 3500);
    };

    useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

    /* ── Delete confirmation modal ── */
    const [deleteDoc, setDeleteDoc] = useState(null);       // null = closed
    const [deleteLoading, setDeleteLoading] = useState(false);

    /* ── Outside-click closes the filter panel ── */
    useEffect(() => {
        if (!showDocFilter) return;
        const handler = (e) => {
            if (filterAreaRef.current && !filterAreaRef.current.contains(e.target)) {
                setShowDocFilter(false);
            }
        };
        /* defer so the opening click (on input / button) isn't caught immediately */
        const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
        return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
    }, [showDocFilter]);

    useEffect(() => { if (!employee) fetchEmployee(); }, []);
    useEffect(() => {
        const empId = employee?.employeeId;
        if (empId) fetchCertificates(empId, 'all');
    }, [employee]);

    const fetchEmployee = async () => {
        try { setEmployee((await api.get(`/user/${id}`)).data); }
        catch (e) { console.error('EMPLOYEE ERROR:', e); }
    };

    const fetchCertificates = async (employeeId, category = 'all') => {
        setDocsLoading(true);
        try {
            let url = `/certificate/employee/${employeeId}`;
            if (category === 'educational') url += '/educational';
            if (category === 'experience') url += '/experience';
            const raw = (await api.get(url)).data || [];
            // Backend soft-delete sets status = REVOKED but the list endpoint
            // returns ALL records including REVOKED — filter them out here.
            const data = raw.filter(d => d.status !== 'REVOKED');
            setAllDocuments(data);
            setDocuments(data);
            setDocType('');
            setDocStatus('');
        } catch (e) {
            console.error('CERTIFICATE ERROR:', e);
            setAllDocuments([]); setDocuments([]);
        } finally { setDocsLoading(false); }
    };

    /* ── Filter using the two dedicated endpoints ──────────────────────────
       GET /api/certificate/type/{certificateType}   → global list by type
       GET /api/certificate/status/{status}          → global list by status

       Both return ALL employees' certs, so we intersect by certificate id
       with allDocuments (which is already scoped to this employee).
    ──────────────────────────────────────────────────────────────────────── */
    const applyDocFilters = async (type, status) => {
        setDocsLoading(true);
        try {
            let base = allDocuments; // start with this employee's full list

            if (type) {
                // Fetch all certs of this type, then keep only those belonging to this employee
                const res = await api.get(`/certificate/type/${type}`);
                const ids = new Set((res.data || []).map(d => d.id));
                base = base.filter(d => ids.has(d.id));
            }

            if (status) {
                // Fetch all certs of this status, then intersect
                const res = await api.get(`/certificate/status/${status}`);
                const ids = new Set((res.data || []).map(d => d.id));
                base = base.filter(d => ids.has(d.id));
            }

            setDocuments(base);
        } catch (e) {
            console.error('FILTER ERROR:', e);
            // Graceful fallback: client-side filter so UI doesn't break
            let f = allDocuments;
            if (type) f = f.filter(d => d.certificateType === type);
            if (status === 'EXPIRED') f = f.filter(d => d.expiryDate && new Date(d.expiryDate) < new Date());
            if (status === 'ACTIVE') f = f.filter(d => !d.expiryDate || new Date(d.expiryDate) >= new Date());
            if (status === 'INACTIVE') f = f.filter(d => d.isActive === false);
            setDocuments(f);
        } finally { setDocsLoading(false); }
    };

    const uniqueCertTypes = [...new Set(allDocuments.map(d => d.certificateType).filter(Boolean))].sort();

    /* open edit modal pre-filled */
    const openEditModal = (doc) => {
        setEditDoc(doc);
        setEditError('');
        setEditForm({
            certificateName: doc.certificateName || '',
            certificateType: doc.certificateType || '',
            issuingAuthority: doc.issuingAuthority || '',
            issueDate: doc.issueDate ? String(doc.issueDate).slice(0, 10) : '',
            expiryDate: doc.expiryDate ? String(doc.expiryDate).slice(0, 10) : '',
            certificateFilePath: doc.certificateFilePath || '',
            remarks: doc.remarks || '',
        });
    };

    /* PUT /api/certificate/{id} */
    const handleEditSave = async () => {
        if (!editDoc) return;
        setEditSaving(true);
        setEditError('');
        try {
            // DTO requires @NotBlank employeeId and @NotNull certificateType (enum)
            const payload = {
                employeeId: editDoc.employeeId || employee?.employeeId || '',
                certificateName: editForm.certificateName,
                certificateType: editForm.certificateType,   // must be enum string e.g. DEGREE_CERTIFICATE
                issuingAuthority: editForm.issuingAuthority || null,
                issueDate: editForm.issueDate || null,
                expiryDate: editForm.expiryDate || null,
                certificateFilePath: editForm.certificateFilePath || null,
                remarks: editForm.remarks || null,
            };
            await api.put(`/certificate/${editDoc.id}`, payload);
            // Update local state immediately
            const updated = { ...editDoc, ...editForm };
            setAllDocuments(prev => prev.map(d => d.id === editDoc.id ? updated : d));
            setDocuments(prev => prev.map(d => d.id === editDoc.id ? updated : d));
            setEditDoc(null);
            showToast('Certificate updated successfully!');
        } catch (e) {
            console.error('EDIT ERROR:', e);
            const msg = e?.response?.data;
            // Spring validation errors come as { field: "message" } object
            if (msg && typeof msg === 'object') {
                const errors = Object.entries(msg).map(([k, v]) => `${k}: ${v}`).join('\n');
                setEditError(errors);
            } else {
                setEditError(typeof msg === 'string' ? msg : 'Update failed. Please try again.');
            }
        } finally {
            setEditSaving(false);
        }
    };

    /* DELETE /api/certificate/{id}  (soft-delete) */
    const handleDeleteConfirm = async () => {
        if (!deleteDoc) return;
        setDeleteLoading(true);
        try {
            await api.delete(`/certificate/${deleteDoc.id}`);
            // Remove from local state immediately (optimistic)
            setAllDocuments(prev => prev.filter(d => d.id !== deleteDoc.id));
            setDocuments(prev => prev.filter(d => d.id !== deleteDoc.id));
            const empId = employee?.employeeId;
            setDeleteDoc(null);
            showToast('Certificate deleted successfully!');
            // Refetch to ensure server-state and local-state are in sync
            // (backend soft-delete keeps the record; fetchCertificates filters REVOKED)
            if (empId) fetchCertificates(empId, docCategory);
        } catch (e) {
            console.error('DELETE ERROR:', e);
            const msg = e?.response?.data;
            showToast(typeof msg === 'string' ? msg : 'Delete failed. Please try again.', 'error');
        } finally {
            setDeleteLoading(false);
        }
    };
    /* open panel — used by both search focus and filter icon */
    const openFilter = () => setShowDocFilter(true);
    /* toggle panel — filter icon closes it if already open */
    const toggleFilter = (e) => {
        e.preventDefault();        // prevent input blur from firing
        setShowDocFilter(v => !v);
    };

    const clearAll = () => {
        setDocSearch(''); setDocCategory('all'); setDocType(''); setDocStatus('');
        setShowDocFilter(false);
        const empId = employee?.employeeId;
        if (empId) fetchCertificates(empId, 'all');
    };

    /* ── Download ── */
    const handleDownload = async (doc) => {
        const key = `${doc.id}_download`;
        setActionLoading(p => ({ ...p, [key]: true }));
        try {
            const res = await api.get(`/certificate/download/${doc.id}`, { responseType: 'blob' });
            const disp = res.headers['content-disposition'];
            let fname = doc.certificateName || 'certificate';
            if (disp) { const m = disp.match(/filename="?([^"]+)"?/); if (m?.[1]) fname = m[1]; }
            else if (doc.certificateFilePath) fname = doc.certificateFilePath.split('/').pop();
            const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
            const link = Object.assign(document.createElement('a'), { href: url });
            link.setAttribute('download', fname);
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { console.error('DOWNLOAD ERROR:', e); toast.error('Failed to download the file.'); }
        finally { setActionLoading(p => ({ ...p, [key]: false })); }
    };

    /* ── View ── */
    const handleView = async (doc) => {
        const key = `${doc.id}_view`;
        setActionLoading(p => ({ ...p, [key]: true }));
        setPreviewLoading(true); setPreviewError(''); setPreview(null);
        try {
            const res = await api.get(`/certificate/download/${doc.id}`, { responseType: 'blob' });
            const ct = res.headers['content-type'] || 'application/octet-stream';
            const ext = getFileExtension(doc.certificateFilePath);
            const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            const isPdf = ext === 'pdf' || ct.includes('pdf');
            const objUrl = window.URL.createObjectURL(new Blob([res.data], { type: ct }));
            if (!isImg && !isPdf) { window.URL.revokeObjectURL(objUrl); await handleDownload(doc); return; }
            setPreview({ url: objUrl, type: isPdf ? 'pdf' : 'image', name: doc.certificateName || 'Document' });
        } catch (e) { console.error('VIEW ERROR:', e); setPreviewError('Failed to load the document.'); }
        finally { setPreviewLoading(false); setActionLoading(p => ({ ...p, [key]: false })); }
    };

    const closePreview = () => {
        if (preview?.url) window.URL.revokeObjectURL(preview.url);
        setPreview(null); setPreviewError('');
    };

    const getInitials = (emp) => {
        if (!emp) return '?';
        if (emp.firstName && emp.lastName) return `${emp.firstName[0]}${emp.lastName[0]}`.toUpperCase();
        if (emp.fullName) return emp.fullName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
        return '?';
    };
    const getFullName = (emp) => {
        if (!emp) return '—';
        if (emp.firstName && emp.lastName)
            return `${emp.firstName}${emp.middleName ? ' ' + emp.middleName : ''} ${emp.lastName}`;
        return emp.fullName || '—';
    };
    const getStatus = (emp) => {
        if (!emp) return null;
        if (emp.isActive !== undefined) return emp.isActive ? 'Active' : 'Inactive';
        return emp.status || null;
    };

    /* ══════════════ Tab content ══════════════ */
    const renderTabContent = () => {
        if (!employee) return null;
        switch (activeTab) {

            case 'personal': return (
                <div className="ep-tab-content">
                    <div className="ep-tab-header"><h3>Personal Information</h3></div>
                    <div className="ep-info-grid">
                        <ReadOnly label="First Name" value={employee.firstName || employee.fullName?.split(' ')[0]} />
                        <ReadOnly label="Middle Name" value={employee.middleName} />
                        <ReadOnly label="Last Name" value={employee.lastName || employee.fullName?.split(' ').slice(-1)[0]} />
                        <ReadOnly label="Date of Birth" value={formatDate(employee.dateOfBirth)} />
                        <ReadOnly label="Gender" value={employee.gender} />
                        <ReadOnly label="Email" value={employee.email} />
                        <ReadOnly label="Phone Number" value={employee.phoneNumber || employee.phone} />
                        <ReadOnly label="Alternate Phone" value={employee.alternatePhone} />
                    </div>
                </div>
            );

            case 'address': return (
                <div className="ep-tab-content">
                    <div className="ep-tab-header"><h3>Home Address</h3></div>
                    <div className="ep-info-grid">
                        <ReadOnly label="Address Line 1" value={employee.addressLine1 || employee.address} />
                        <ReadOnly label="Address Line 2" value={employee.addressLine2} />
                        <ReadOnly label="City" value={employee.city} />
                        <ReadOnly label="State" value={employee.state} />
                        <ReadOnly label="Postal Code" value={employee.postalCode || employee.pincode} />
                        <ReadOnly label="Country" value={employee.country} />
                    </div>
                </div>
            );

            case 'job': return (
                <div className="ep-tab-content">
                    <div className="ep-tab-header"><h3>Job Information</h3></div>
                    <div className="ep-info-grid">
                        <ReadOnly label="Employee ID" value={employee.employeeId} />
                        <ReadOnly label="Job Title" value={employee.jobTitle} />
                        <ReadOnly label="Department" value={employee.department} />
                        <ReadOnly label="Role" value={employee.role} />
                        <ReadOnly label="Employment Type" value={employee.employmentType} />
                        <ReadOnly label="Work Location" value={employee.workLocation} />
                        <ReadOnly label="Manager ID" value={employee.managerId} />
                        <ReadOnly label="Hire Date" value={formatDate(employee.hireDate || employee.joinDate)} />
                        <ReadOnly label="Status" value={getStatus(employee)} />
                    </div>
                </div>
            );

            case 'salary': return (
                <div className="ep-tab-content">
                    <div className="ep-tab-header"><h3>Salary Information</h3></div>
                    <div className="ep-info-grid">
                        <ReadOnly label="Salary" value={formatSalary(employee.salary)} />
                        <ReadOnly label="Bank Name" value={employee.bankName} />
                        <ReadOnly label="Account Number" value={employee.bankAccountNumber} />
                        <ReadOnly label="IFSC Code" value={employee.ifscCode} />
                    </div>
                </div>
            );

            case 'emergency': return (
                <div className="ep-tab-content">
                    <div className="ep-tab-header"><h3>Emergency Contact</h3></div>
                    <div className="ep-info-grid">
                        <ReadOnly label="Contact Name" value={employee.emergencyContactName} />
                        <ReadOnly label="Contact Phone" value={employee.emergencyContactPhone} />
                        <ReadOnly label="Relationship" value={employee.emergencyContactRelationship} />
                    </div>
                </div>
            );

            case 'documents': {
                const activeFilterCount = [docCategory !== 'all', docType !== '', docStatus !== ''].filter(Boolean).length;
                const visibleDocs = documents.filter(doc =>
                    !docSearch.trim() ||
                    doc.certificateName?.toLowerCase().includes(docSearch.toLowerCase()) ||
                    doc.certificateType?.toLowerCase().includes(docSearch.toLowerCase()) ||
                    doc.issuingAuthority?.toLowerCase().includes(docSearch.toLowerCase())
                );

                return (
                    <div className="ep-tab-content">

                        {/* ═══ Search bar + filter icon + filter panel
                            All inside filterAreaRef — outside click closes panel ═══ */}
                        <div className="ep-docs-filter-area" ref={filterAreaRef}>

                            {/* Row: [search input] [filter icon] */}
                            <div className="ep-docs-search-row">

                                {/* Search input — focus opens panel */}
                                <div className="ep-docs-search">
                                    <FiSearch className="ep-docs-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, type, authority…"
                                        value={docSearch}
                                        onChange={e => setDocSearch(e.target.value)}
                                        onFocus={openFilter}
                                    />
                                    {(docSearch || activeFilterCount > 0) && (
                                        <button
                                            className="ep-docs-search-clear"
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={clearAll}
                                            title="Clear all"
                                        >
                                            <FiX size={13} />
                                        </button>
                                    )}
                                </div>

                                {/* Filter icon — toggles panel; shows ✕ when open */}
                                <button
                                    className={`ep-docs-filter-btn${showDocFilter || activeFilterCount > 0 ? ' ep-docs-filter-btn--active' : ''}`}
                                    onMouseDown={toggleFilter}
                                    title={showDocFilter ? 'Close filters' : 'Open filters'}
                                >
                                    {showDocFilter
                                        ? <FiX size={15} />
                                        : <FiFilter size={15} />
                                    }
                                    {!showDocFilter && activeFilterCount > 0 && (
                                        <span className="ep-docs-filter-badge">{activeFilterCount}</span>
                                    )}
                                </button>
                            </div>

                            {/* Filter panel — 3 selects side by side, below search row */}
                            {showDocFilter && (
                                <div className="ep-docs-filter-panel">

                                    <div className="ep-docs-filter-row">

                                        {/* Category — server-side fetch */}
                                        <div className="ep-docs-filter-col">
                                            <label className="ep-docs-filter-col-label">Category</label>
                                            <select
                                                value={docCategory}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    setDocCategory(v);
                                                    setDocType(''); setDocStatus('');
                                                    const empId = employee?.employeeId;
                                                    if (empId) fetchCertificates(empId, v);
                                                }}
                                            >
                                                <option value="all">All Certificates</option>
                                                <option value="educational">Educational</option>
                                                <option value="experience">Experience</option>
                                            </select>
                                        </div>

                                        {/* Certificate Type — client-side */}
                                        <div className="ep-docs-filter-col">
                                            <label className="ep-docs-filter-col-label">Certificate Type</label>
                                            <select
                                                value={docType}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    setDocType(v);
                                                    applyDocFilters(v, docStatus);
                                                }}
                                            >
                                                <option value="">All Types</option>
                                                {uniqueCertTypes.map(t => (
                                                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Status — client-side */}
                                        <div className="ep-docs-filter-col">
                                            <label className="ep-docs-filter-col-label">Status</label>
                                            <select
                                                value={docStatus}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    setDocStatus(v);
                                                    applyDocFilters(docType, v);
                                                }}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="ACTIVE">Active</option>
                                                <option value="INACTIVE">Inactive</option>
                                                <option value="EXPIRED">Expired</option>
                                            </select>
                                        </div>

                                    </div>

                                    {activeFilterCount > 0 && (
                                        <div className="ep-docs-filter-footer">
                                            <button className="ep-docs-filter-clear-btn" onClick={clearAll}>
                                                <FiX size={11} /> Clear all filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Active chips */}
                        {activeFilterCount > 0 && (
                            <div className="ep-docs-active-filter">
                                {docCategory !== 'all' && (
                                    <span className="ep-docs-chip">
                                        {docCategory === 'educational' ? 'Educational' : 'Experience'}
                                        <button onClick={() => { setDocCategory('all'); const e = employee?.employeeId; if (e) fetchCertificates(e, 'all'); }}><FiX size={11} /></button>
                                    </span>
                                )}
                                {docType && (
                                    <span className="ep-docs-chip">
                                        {docType.replace(/_/g, ' ')}
                                        <button onClick={() => { setDocType(''); applyDocFilters('', docStatus); }}><FiX size={11} /></button>
                                    </span>
                                )}
                                {docStatus && (
                                    <span className="ep-docs-chip ep-docs-chip--status">
                                        {docStatus}
                                        <button onClick={() => { setDocStatus(''); applyDocFilters(docType, ''); }}><FiX size={11} /></button>
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Table */}
                        {docsLoading ? (
                            <div className="ep-state"><div className="ep-spinner" /><p>Loading documents…</p></div>
                        ) : visibleDocs.length === 0 ? (
                            <div className="ep-docs-empty">
                                <FiFileText size={36} style={{ color: '#d1d5db', marginBottom: 8 }} />
                                <p>{allDocuments.length === 0
                                    ? 'No certificates uploaded for this employee.'
                                    : 'No certificates match your search or filters.'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="ep-docs-table-wrap">
                                    <table className="ep-docs-table">
                                        <thead>
                                            <tr>
                                                <th>#</th><th>Certificate Name</th><th>Type</th>
                                                <th>Authority</th><th>Issue Date</th><th>Expiry Date</th><th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleDocs.map((doc, idx) => {
                                                const hasFile = !!doc.certificateFilePath;
                                                const vk = `${doc.id}_view`, dk = `${doc.id}_download`;
                                                const expired = doc.expiryDate && new Date(doc.expiryDate) < new Date();
                                                const ext = getFileExtension(doc.certificateFilePath);
                                                return (
                                                    <tr key={doc.id}>
                                                        <td className="ep-dt-num">{idx + 1}</td>
                                                        <td className="ep-dt-name">
                                                            <span className="ep-dt-file-icon">
                                                                {ext === 'pdf' ? '📄' : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? '🖼️' : '📋'}
                                                            </span>
                                                            {fmt(doc.certificateName)}
                                                        </td>
                                                        <td><span className="ep-doc-type">{fmt(doc.certificateType).replace(/_/g, ' ')}</span></td>
                                                        <td>{fmt(doc.issuingAuthority)}</td>
                                                        <td>{formatDate(doc.issueDate)}</td>
                                                        <td className={expired ? 'ep-doc-expired' : ''}>
                                                            {doc.expiryDate ? formatDate(doc.expiryDate) : '—'}
                                                            {expired && <span className="ep-expired-tag">Expired</span>}
                                                        </td>
                                                        <td>
                                                            <div className="ep-doc-actions">
                                                                <button className="ep-doc-btn ep-doc-btn--view"
                                                                    title={hasFile ? 'Preview' : 'No file uploaded'}
                                                                    disabled={!hasFile || !!actionLoading[vk]}
                                                                    onClick={() => handleView(doc)}>
                                                                    {actionLoading[vk] ? <span className="ep-btn-spinner" /> : <FiEye />}
                                                                </button>
                                                                <button className="ep-doc-btn ep-doc-btn--download"
                                                                    title={hasFile ? 'Download' : 'No file uploaded'}
                                                                    disabled={!hasFile || !!actionLoading[dk]}
                                                                    onClick={() => handleDownload(doc)}>
                                                                    {actionLoading[dk] ? <span className="ep-btn-spinner" /> : <FiDownload />}
                                                                </button>
                                                                <button className="ep-doc-btn ep-doc-btn--edit"
                                                                    title="Edit certificate"
                                                                    onClick={() => openEditModal(doc)}>
                                                                    <FiEdit2 />
                                                                </button>
                                                                <button className="ep-doc-btn ep-doc-btn--delete"
                                                                    title="Delete certificate"
                                                                    onClick={() => setDeleteDoc(doc)}>
                                                                    <FiTrash2 />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="ep-docs-count">
                                    Showing {visibleDocs.length} of {allDocuments.length} certificate{allDocuments.length !== 1 ? 's' : ''}
                                </div>
                            </>
                        )}
                    </div>
                );
            }

            default: return null;
        }
    };

    if (!employee) return (
        <div className="hr-content animate-fade-in">
            <div className="ep-state"><div className="ep-spinner" /><p>Loading employee profile…</p></div>
        </div>
    );

    const status = getStatus(employee);

    return (
        <div className="hr-content animate-fade-in">
            <div className="ep-wrapper">

                <div className="ep-page-title-row">
                    <button className="ep-back-btn" onClick={() => navigate('/hr/teams')} title="Go back"><FiArrowLeft /></button>
                    <h2 className="ep-page-title">Employee Profile</h2>
                </div>

                <div className="ep-profile-container">

                    {/* LEFT PANEL */}
                    <div className="ep-profile-left">
                        <div className="ep-photo-wrap">
                            <div className="ep-photo-placeholder">{getInitials(employee)}</div>
                        </div>
                        <div className="ep-basic-info">
                            <h3>{getFullName(employee)}</h3>
                            <p className="ep-position">{employee.jobTitle || employee.role || '—'}</p>
                            <div className="ep-info-item"><label>Employee ID</label><p>{fmt(employee.employeeId)}</p></div>
                            <div className="ep-info-item"><label>Email</label><p>{fmt(employee.email)}</p></div>
                            <div className="ep-info-item"><label>Phone</label><p>{fmt(employee.phoneNumber || employee.phone)}</p></div>
                            <div className="ep-info-item"><label>Department</label><p>{fmt(employee.department)}</p></div>
                            <div className="ep-info-item">
                                <label>Status</label>
                                {status
                                    ? <p className={`ep-status-badge ${status === 'Active' ? 'ep-status--active' : 'ep-status--inactive'}`}>{status}</p>
                                    : <p>—</p>}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT PANEL */}
                    <div className="ep-profile-right">
                        <div className="ep-tabs">
                            {TABS.map(({ key, label }) => (
                                <button key={key}
                                    className={`ep-tab-button${activeTab === key ? ' active' : ''}`}
                                    onClick={() => setActiveTab(key)}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="ep-tab-content-container">{renderTabContent()}</div>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {(preview || previewLoading || previewError) && (
                <div className="ep-modal-overlay" onClick={closePreview}>
                    <div className="ep-modal" onClick={e => e.stopPropagation()}>
                        <div className="ep-modal-header">
                            <span className="ep-modal-title">{preview?.name || 'Document Preview'}</span>
                            <div className="ep-modal-header-actions">
                                {preview && <a href={preview.url} download={preview.name} className="ep-modal-dl-btn"><FiDownload /> Download</a>}
                                <button className="ep-modal-close" onClick={closePreview}>✕</button>
                            </div>
                        </div>
                        <div className="ep-modal-body">
                            {previewLoading && <div className="ep-state"><div className="ep-spinner" /><p>Loading document…</p></div>}
                            {previewError && <div className="ep-state ep-state--error"><p>{previewError}</p></div>}
                            {preview?.type === 'pdf' && <iframe src={preview.url} title={preview.name} className="ep-modal-iframe" />}
                            {preview?.type === 'image' && <div className="ep-modal-img-wrap"><img src={preview.url} alt={preview.name} className="ep-modal-img" /></div>}
                        </div>
                    </div>
                </div>
            )}

            {/* ══ EDIT CERTIFICATE MODAL ══ */}
            {editDoc && (
                <div className="ep-modal-overlay" onClick={() => setEditDoc(null)}>
                    <div className="ep-modal ep-cert-modal" onClick={e => e.stopPropagation()}>

                        <div className="ep-modal-header">
                            <span className="ep-modal-title">Edit Certificate</span>
                            <button className="ep-modal-close" onClick={() => setEditDoc(null)}>✕</button>
                        </div>

                        <div className="ep-cert-modal-body">
                            {editError && <div className="ep-cert-error">{editError}</div>}
                            <div className="ep-cert-form-grid">

                                <div className="ep-cert-field">
                                    <label>Certificate Name <span className="ep-cert-req">*</span></label>
                                    <input type="text" value={editForm.certificateName}
                                        onChange={e => setEditForm(p => ({ ...p, certificateName: e.target.value }))}
                                        placeholder="e.g. AWS Solutions Architect" />
                                </div>

                                <div className="ep-cert-field">
                                    <label>Certificate Type <span className="ep-cert-req">*</span></label>
                                    <select value={editForm.certificateType}
                                        onChange={e => setEditForm(p => ({ ...p, certificateType: e.target.value }))}>
                                        <option value="">— Select Type —</option>
                                        <optgroup label="Educational">
                                            <option value="TENTH_CERTIFICATE">10th Certificate</option>
                                            <option value="TWELFTH_CERTIFICATE">12th / Intermediate Certificate</option>
                                            <option value="DIPLOMA_CERTIFICATE">Diploma Certificate</option>
                                            <option value="DEGREE_CERTIFICATE">Degree Certificate</option>
                                            <option value="POST_GRADUATION_CERTIFICATE">Post Graduation Certificate</option>
                                            <option value="PHD_CERTIFICATE">PhD Certificate</option>
                                        </optgroup>
                                        <optgroup label="Professional">
                                            <option value="EXPERIENCE_CERTIFICATE">Experience Certificate</option>
                                            <option value="RELIEVING_LETTER">Relieving Letter</option>
                                            <option value="SALARY_CERTIFICATE">Salary Certificate</option>
                                        </optgroup>
                                        <optgroup label="Technical">
                                            <option value="TECHNICAL_CERTIFICATION">Technical Certification</option>
                                            <option value="PROFESSIONAL_CERTIFICATION">Professional Certification</option>
                                            <option value="TRAINING_CERTIFICATE">Training Certificate</option>
                                        </optgroup>
                                        <optgroup label="Government">
                                            <option value="AADHAAR_CARD">Aadhaar Card</option>
                                            <option value="PAN_CARD">PAN Card</option>
                                            <option value="PASSPORT">Passport</option>
                                            <option value="DRIVING_LICENSE">Driving License</option>
                                            <option value="VOTER_ID">Voter ID</option>
                                        </optgroup>
                                        <optgroup label="Other">
                                            <option value="MEDICAL_CERTIFICATE">Medical Certificate</option>
                                            <option value="CHARACTER_CERTIFICATE">Character Certificate</option>
                                            <option value="CASTE_CERTIFICATE">Caste Certificate</option>
                                            <option value="INCOME_CERTIFICATE">Income Certificate</option>
                                            <option value="RESIDENCE_CERTIFICATE">Residence Certificate</option>
                                            <option value="OTHER">Other</option>
                                        </optgroup>
                                    </select>
                                </div>

                                <div className="ep-cert-field">
                                    <label>Issuing Authority</label>
                                    <input type="text" value={editForm.issuingAuthority}
                                        onChange={e => setEditForm(p => ({ ...p, issuingAuthority: e.target.value }))}
                                        placeholder="e.g. Amazon Web Services" />
                                </div>

                                <div className="ep-cert-field">
                                    <label>File Path</label>
                                    <input type="text" value={editForm.certificateFilePath}
                                        onChange={e => setEditForm(p => ({ ...p, certificateFilePath: e.target.value }))}
                                        placeholder="uploads/certificates/..." />
                                </div>

                                <div className="ep-cert-field">
                                    <label>Issue Date</label>
                                    <input type="date" value={editForm.issueDate}
                                        onChange={e => setEditForm(p => ({ ...p, issueDate: e.target.value }))} />
                                </div>

                                <div className="ep-cert-field">
                                    <label>Expiry Date</label>
                                    <input type="date" value={editForm.expiryDate}
                                        onChange={e => setEditForm(p => ({ ...p, expiryDate: e.target.value }))} />
                                </div>

                                <div className="ep-cert-field ep-cert-field--full">
                                    <label>Remarks</label>
                                    <textarea rows={3} value={editForm.remarks}
                                        onChange={e => setEditForm(p => ({ ...p, remarks: e.target.value }))}
                                        placeholder="Any additional notes…" />
                                </div>

                            </div>
                        </div>

                        <div className="ep-cert-modal-footer">
                            <button className="ep-cert-cancel-btn" onClick={() => setEditDoc(null)} disabled={editSaving}>Cancel</button>
                            <button className="ep-cert-save-btn" onClick={handleEditSave} disabled={editSaving}>
                                {editSaving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ DELETE CONFIRM MODAL ══ */}
            {deleteDoc && (
                <div className="ep-modal-overlay" onClick={() => setDeleteDoc(null)}>
                    <div className="ep-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="ep-confirm-icon"><FiTrash2 size={28} /></div>
                        <h3 className="ep-confirm-title">Delete Certificate</h3>
                        <p className="ep-confirm-msg">
                            Are you sure you want to delete <strong>{deleteDoc.certificateName}</strong>?
                            <br /><span className="ep-confirm-sub">This will soft-delete the certificate record.</span>
                        </p>
                        <div className="ep-confirm-actions">
                            <button className="ep-cert-cancel-btn" onClick={() => setDeleteDoc(null)} disabled={deleteLoading}>Cancel</button>
                            <button className="ep-cert-delete-btn" onClick={handleDeleteConfirm} disabled={deleteLoading}>
                                {deleteLoading ? 'Deleting…' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ SUCCESS / ERROR TOAST ══ */}
            {toast && (
                <div className={`ep-toast-fixed ep-toast-fixed--${toast.type}`}>
                    <span className="ep-toast-icon">
                        {toast.type === 'success' ? '✓' : '✕'}
                    </span>
                    {toast.msg}
                    <button className="ep-toast-dismiss" onClick={() => setToast(null)}>✕</button>
                </div>
            )}

        </div>
    );
};

export default ViewEmployeeProfile;

